import React from 'react';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { MessageSquare, Send, X } from 'lucide-react';

const AskAIDrawer = ({ isOpen, onClose }) => {
    const { t } = useTranslation('askAI');
    return (
        <AnimatePresence>
            {isOpen && (
                <>
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 bg-black/20 z-[999] backdrop-blur-sm"
                        onClick={onClose}
                        data-testid="ask-ai-drawer-overlay"
                    />
                    <motion.div
                        initial={{ x: '100%' }}
                        animate={{ x: 0 }}
                        exit={{ x: '100%' }}
                        transition={{ type: 'spring', damping: 25, stiffness: 200 }}
                        className="fixed top-0 right-0 h-full w-[60%] bg-white shadow-2xl z-[1000] flex flex-col"
                        data-testid="ask-ai-drawer"
                    >
                        <div className="p-6 border-b border-gray-100 shrink-0" data-testid="ask-ai-drawer-header">
                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-3">
                                    <div className="p-2 bg-[#4ABDBD]/10 rounded-lg">
                                        <MessageSquare className="text-[#4ABDBD]" size={24} data-testid="ask-ai-drawer-title-icon" />
                                    </div>
                                    <h2 className="text-xl font-bold text-gray-800" data-testid="ask-ai-drawer-title">{t('askAI.ask_ai_assistant')}</h2>
                                </div>
                                <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-full transition-colors cursor-pointer" data-testid="ask-ai-drawer-close-button">
                                    <X className="text-gray-500" size={24} data-testid="ask-ai-drawer-close-icon" />
                                </button>
                            </div>
                        </div>

                        <div className="flex-1 overflow-y-auto p-6 bg-gray-50/50 flex flex-col gap-5" data-testid="ask-ai-drawer-messages-section">
                            <div className="bg-white p-4 rounded-2xl rounded-tl-none shadow-sm border border-gray-100 max-w-[85%] self-start flex gap-3" data-testid="ask-ai-drawer-greeting-message">
                                <div className="shrink-0 pt-1">
                                    <div className="w-6 h-6 rounded-full bg-gradient-to-br from-[#4ABDBD] to-[#3AA8A8] flex items-center justify-center text-white text-[10px] font-bold" data-testid="ask-ai-drawer-greeting-avatar">
                                        {t('askAI.ai_label', 'AI')}
                                    </div>
                                </div>
                                <p className="text-sm text-gray-800 leading-relaxed" data-testid="ask-ai-drawer-greeting-text">{t('askAI.greeting')}</p>
                            </div>
                            <div className="bg-gradient-to-r from-[#4ABDBD] to-[#3AA8A8] text-white p-4 rounded-2xl rounded-tr-none shadow-md max-w-[85%] self-end" data-testid="ask-ai-drawer-sample-question">
                                <p className="text-sm leading-relaxed" data-testid="ask-ai-drawer-sample-question-text">{t('askAI.sample_question')}</p>
                            </div>
                            <div className="bg-white p-4 rounded-2xl rounded-tl-none shadow-sm border border-gray-100 max-w-[85%] self-start flex gap-3" data-testid="ask-ai-drawer-sample-answer">
                                <div className="shrink-0 pt-1">
                                    <div className="w-6 h-6 rounded-full bg-gradient-to-br from-[#4ABDBD] to-[#3AA8A8] flex items-center justify-center text-white text-[10px] font-bold" data-testid="ask-ai-drawer-sample-answer-avatar">
                                        {t('askAI.ai_label', 'AI')}
                                    </div>
                                </div>
                                <p className="text-sm text-gray-800 leading-relaxed" data-testid="ask-ai-drawer-sample-answer-text">{t('askAI.sample_answer')}</p>
                            </div>
                        </div>

                        <div className="p-6 border-t border-gray-100 shrink-0 bg-white" data-testid="ask-ai-drawer-input-section">
                            <div className="flex gap-3 items-center">
                                <input type="text" placeholder={t('askAI.message_placeholder')} className="flex-1 p-3.5 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#4ABDBD]/50 focus:bg-white text-sm text-gray-800 transition-all" data-testid="ask-ai-drawer-message-input" />
                                <button className="p-3.5 bg-gradient-to-br from-[#4ABDBD] to-[#3AA8A8] text-white rounded-xl shadow-md hover:shadow-lg hover:from-[#3AA8A8] hover:to-[#2D8A8A] transition-all cursor-pointer" data-testid="ask-ai-drawer-send-button">
                                    <Send size={20} data-testid="ask-ai-drawer-send-icon" />
                                </button>
                            </div>
                        </div>
                    </motion.div>
                </>
            )}
        </AnimatePresence>
    );
};

export default AskAIDrawer;
